import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Shield, Check, X, Save, RefreshCw, Users, UserCog, Edit2, Trash2, Globe, Clock, MapPin as MapPinIcon, AlertTriangle, Building2, Activity, Heart, ShieldAlert, Languages } from 'lucide-react';
import { applyLanguage, getLanguageMode } from '../i18n';
import { useNavigate } from 'react-router-dom';

const Settings = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('language');
  const [permissions, setPermissions] = useState({});
  const [userPermissions, setUserPermissions] = useState([]);
  const [availablePages, setAvailablePages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeRole, setActiveRole] = useState('manager');
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Get current user role from localStorage
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser.role === 'admin' || currentUser.role === 'pilot_admin';

  const roles = [
    { id: 'pilot_admin', label: 'Pilot Admin', color: 'bg-fuchsia-500' },
    { id: 'home', label: 'Home', color: 'bg-red-500' },
    { id: 'manager', label: 'Manager', color: 'bg-blue-500' },
    { id: 'accountant', label: 'Accountant', color: 'bg-green-500' },
    { id: 'project_manager', label: 'Project Manager', color: 'bg-purple-500' },
    { id: 'sales_rep', label: 'Sales Rep', color: 'bg-orange-500' },
    { id: 'technical', label: 'Technical', color: 'bg-cyan-500' },
    { id: 'hr', label: 'HR', color: 'bg-pink-500' },
    { id: 'engineer', label: 'Engineer', color: 'bg-indigo-500' },
    { id: 'assistant', label: 'Assistant', color: 'bg-teal-500' }
  ];

  const pageLabels = {
    home: 'Home',
    dashboard: 'Dashboard',
    cfo_dashboard: 'CFO Dashboard (لوحة المدير المالي)',
    reconciliation_center: 'Reconciliation Center (مركز تسوية الحقيقة المالية)',
    financial_repair_center: 'Financial Repair Center (مركز الإصلاح المالي)',
    data_quality_center: 'Data Quality Center (مركز جودة البيانات)',
    vo_queue: 'VO Posting Queue (طابور ترحيل أوامر التغيير)',
    leads_console: 'Leads → Quotation (تحويل الـ Leads إلى عروض)',
    cfo_monthly_pack: 'CFO Monthly Pack (الحزمة الشهرية للمدير المالي)',
    ai_decision_cockpit: 'AI Decision Cockpit (مركز قرارات الذكاء الاصطناعي)',
    action_center: 'Action Center (مركز الإجراءات)',
    approvals: 'Approvals',
    projects: 'Projects',
    project_execution_center: 'Project Execution Center (PEC) (مركز تنفيذ المشاريع)',
    contracts: 'Contracts',
    contract_execution: 'Contract Execution (تنفيذ العقد)',
    quotations: 'Quotations',
    quotation_analytics: 'Quotation Analytics (تحليلات العروض)',
    rfq: 'Request for Quotation (طلب عرض سعر)',
    invoices: 'Invoices',
    commercial_hub: 'Commercial Hub (المركز التجاري)',
    credit_control: 'Credit Control (متابعة الذمم المدينة)',
    credit_notes: 'Credit Notes (إشعارات دائنة)',
    zatca: 'ZATCA (هيئة الزكاة)',
    zatca_setup: 'ZATCA Setup (إعداد هيئة الزكاة)',
    compliance_center: 'Compliance Center (مركز الالتزام)',
    audit_trail: 'Audit Trail (مسار التدقيق)',
    purchase_orders: 'Purchase Orders',
    supplier_bills: 'Supplier Bills',
    vendor_quotations: 'Vendor Quotations (عروض الموردين)',
    supplier_offers_comparison: 'Comparison of Supplier Offers (مقارنة عروض الموردين)',
    customers: 'Customers',
    suppliers: 'Suppliers',
    accounts: 'Accounts',
    bank: 'Bank',
    payments: 'Payments',
    journal: 'Journal',
    users: 'Users',
    role_permissions: 'Role Permissions (صلاحيات الأدوار)',
    employee_permissions: 'Employee Permissions (صلاحيات الموظفين)',
    employees: 'Employees',
    payroll: 'Payroll',
    inventory: 'Inventory',
    expenses: 'Expenses',
    expenses_report: 'Expenses Report',
    reports: 'Reports',
    settings: 'Settings',
    petty_cash: 'Petty Cash',
    employee_accounts: 'Employee Accounts',
    assets: 'Assets',
    budgets: 'Budgets',
    backup: 'Backup (النسخ الاحتياطي)',
    visit_form: 'Visit Form (نموذج الزيارة)',
    visit_reports_system: 'Visit Reports (تقارير الزيارات)',
    field_visits: 'Field Visits (الزيارات الميدانية)',
    vat_return: 'VAT Return (إقرار ضريبة القيمة المضافة)',
    year_end_closing: 'Year-End Closing (إقفال نهاية السنة)',
    external_accountant_workspace: 'External Accountant Workspace (مكتب المحاسب الخارجي)',
    // ── Newly added modules (2026-02) ──────────────────────────────────────
    subcontractors: 'Subcontractors (مقاولو الباطن)',
    subcontracts: 'Subcontracts (عقود الباطن)',
    subcontract_claims: 'Subcontract Claims (مستخلصات الباطن)',
    documents: 'Documents (المستندات)',
    notifications: 'Notifications (التنبيهات)',
    month_end: 'Month-End Closing (إقفال نهاية الشهر)',
    fleet: 'Fleet (أسطول المركبات)',
    audit_log: 'Audit Log (سجل التدقيق)',
    security: 'Security & Sessions (الأمان والجلسات)',
    security_dashboard: 'Security Dashboard (لوحة الأمان)',
    instant_reports: 'Instant Reports (التقارير الفورية)',
    governance: 'Governance (الحوكمة)',
    accounting_reports: 'Accounting Reports (التقارير المحاسبية)',
    cost_centers: 'Cost Centers (مراكز التكلفة)',
    cost_allocation: 'Cost Allocation (توزيع التكاليف)',
    progress_claims: 'Progress Claims (مستخلصات الإنجاز)',
    variation_orders: 'Variation Orders (أوامر التغيير)',
    equipment: 'Equipment (المعدات)',
    customer_report: 'Customer Report (تقرير العملاء)',
    supplier_report: 'Supplier Report (تقرير الموردين)',
    my_tasks: 'My Tasks (مهامي)',
    data_integrity: 'Data Integrity (سلامة البيانات)',
    audit_intelligence: 'Audit Intelligence (ذكاء التدقيق)',
    // ── 2026-06 batch (Pilot+ catalog expansion, 20 new IDs) ──────────────
    my_work_queue: 'My Work Queue (قائمة عملي)',
    cfo_pnl: 'CFO PnL (الربح والخسارة للمدير المالي)',
    insights: 'Insights (الرؤى التحليلية)',
    approval_inbox: 'Approval Inbox (صندوق الموافقات)',
    approval_matrix: 'Approval Matrix (مصفوفة الموافقات)',
    item_master: 'Item Master (دليل الأصناف)',
    rfq_workspace: 'RFQ Workspace (مساحة طلبات عروض الأسعار)',
    s2p_review_queue: 'S2P Review Queue (قائمة مراجعة المشتريات)',
    s2p_effectiveness: 'S2P Effectiveness (فعالية المشتريات)',
    mar_review_queue: 'MAR Review Queue (قائمة مراجعة المواد المستلمة)',
    mir_review_queue: 'MIR Review Queue (قائمة مراجعة المواد الصادرة)',
    delivery_dashboard: 'Delivery Dashboard (لوحة التسليمات)',
    consumption_reality: 'Consumption Reality (حقيقة الاستهلاك)',
    pece_claims: 'PECE Claims (مستخلصات تنفيذ المشاريع)',
    financial_truth: 'Financial Truth (الحقيقة المالية)',
    event_inspector: 'Event Inspector (متفحّص الأحداث)',
    demo_center: 'Demo Center (مركز العرض التجريبي)',
    legacy_recovery: 'Legacy Recovery (استرداد بيانات قديمة)',
    archive_center: 'Archive Center (مركز الأرشيف)',
    company_onboarding: 'Company Onboarding (إعداد الشركة)',
  };

  useEffect(() => {
    fetchPermissions();
    fetchUserPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      const response = await axiosInstance.get('/permissions');
      setPermissions(response.data.permissions);
      setAvailablePages(response.data.available_pages);
    } catch (error) {
      toast.error('Failed to fetch permissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPermissions = async () => {
    try {
      const response = await axiosInstance.get('/user-permissions');
      setUserPermissions(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch user permissions');
    }
  };

  const togglePage = (role, page) => {
    if (page === 'dashboard') return;
    
    setPermissions(prev => {
      const rolePerms = prev[role] || { pages: [] };
      const currentPages = rolePerms.pages || [];
      
      let newPages;
      if (currentPages.includes(page)) {
        newPages = currentPages.filter(p => p !== page);
      } else {
        newPages = [...currentPages, page];
      }
      
      return {
        ...prev,
        [role]: { ...rolePerms, pages: newPages }
      };
    });
  };

  const toggleUserPage = (userId, page) => {
    if (page === 'dashboard') return;
    
    setUserPermissions(prev => prev.map(user => {
      if (user.user_id === userId) {
        const currentPages = user.pages || [];
        let newPages;
        if (currentPages.includes(page)) {
          newPages = currentPages.filter(p => p !== page);
        } else {
          newPages = [...currentPages, page];
        }
        return { ...user, pages: newPages };
      }
      return user;
    }));
  };

  const savePermissions = async (role) => {
    setSaving(true);
    try {
      const pages = permissions[role]?.pages || [];
      await axiosInstance.put(`/permissions/${role}`, { pages });
      toast.success(`Permissions saved for ${role}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const saveUserPermissions = async (userId) => {
    setSaving(true);
    try {
      const user = userPermissions.find(u => u.user_id === userId);
      const pages = user?.pages || [];
      await axiosInstance.put(`/user-permissions/${userId}`, { pages });
      toast.success('User permissions saved');
      fetchUserPermissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user permissions');
    } finally {
      setSaving(false);
    }
  };

  const resetUserPermissions = async (userId) => {
    try {
      await axiosInstance.delete(`/user-permissions/${userId}`);
      toast.success('Permissions reset to role default');
      fetchUserPermissions();
    } catch (error) {
      toast.error('Failed to reset permissions');
    }
  };

  const selectAll = (role) => {
    setPermissions(prev => ({
      ...prev,
      [role]: { ...prev[role], pages: [...availablePages] }
    }));
  };

  const deselectAll = (role) => {
    setPermissions(prev => ({
      ...prev,
      [role]: { ...prev[role], pages: ['dashboard'] }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div data-testid="settings-page">
      <div className="page-header mb-6">
        <h2 className="page-title flex items-center gap-2">
          <SettingsIcon className="w-6 h-6" />
          {t('settings.page_title')}
        </h2>
      </div>

      {/* Section Tabs */}
      <div className="flex flex-wrap gap-3 mb-6">
        <button
          data-testid="tab-language"
          onClick={() => setActiveSection('language')}
          className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
            activeSection === 'language'
              ? 'bg-purple-600 text-white shadow-lg'
              : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
          }`}
        >
          <Globe className="w-5 h-5" />
          {t('settings.tabs.language_region')}
        </button>
        {isAdmin && (
          <button
            data-testid="tab-company-profile"
            onClick={() => navigate('/company-profile')}
            className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all bg-white text-gray-700 hover:bg-indigo-50 border border-gray-200"
          >
            <Building2 className="w-5 h-5 text-indigo-600" />
            Company Profile
          </button>
        )}
        {isAdmin && (
          <button
            data-testid="tab-smoke-test"
            onClick={() => navigate('/smoke-test')}
            className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all bg-white text-gray-700 hover:bg-emerald-50 border border-gray-200"
          >
            <Activity className="w-5 h-5 text-emerald-600" />
            Smoke Test
          </button>
        )}
        {isAdmin && (
          <button
            data-testid="tab-health-monitor"
            onClick={() => navigate('/health-monitor')}
            className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all bg-white text-gray-700 hover:bg-rose-50 border border-gray-200"
          >
            <Heart className="w-5 h-5 text-rose-600" />
            Health Monitor
          </button>
        )}
        {isAdmin && (
          <button
            data-testid="tab-hardening"
            onClick={() => navigate('/hardening')}
            className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all bg-white text-gray-700 hover:bg-indigo-50 border border-gray-200"
          >
            <ShieldAlert className="w-5 h-5 text-indigo-600" />
            Hardening
          </button>
        )}
        {isAdmin && (
          <button
            data-testid="tab-companies"
            onClick={() => navigate('/companies')}
            className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all bg-white text-gray-700 hover:bg-indigo-50 border border-gray-200"
          >
            <Building2 className="w-5 h-5 text-indigo-600" />
            Companies
          </button>
        )}
        {isAdmin && (
          <button
            data-testid="tab-migration"
            onClick={() => navigate('/migration-center')}
            className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all bg-white text-gray-700 hover:bg-indigo-50 border border-gray-200"
          >
            <ShieldAlert className="w-5 h-5 text-indigo-600" />
            Migration Center
          </button>
        )}
        {isAdmin && (
          <button
            data-testid="tab-i18n-health"
            onClick={() => navigate('/i18n-health')}
            className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all bg-white text-gray-700 hover:bg-indigo-50 border border-gray-200"
          >
            <Languages className="w-5 h-5 text-indigo-600" />
            i18n Health
          </button>
        )}
        <button
          data-testid="tab-roles"
          onClick={() => setActiveSection('roles')}
          className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
            activeSection === 'roles'
              ? 'bg-purple-600 text-white shadow-lg'
              : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
          }`}
        >
          <Shield className="w-5 h-5" />
          {t('settings.tabs.roles')}
        </button>
        <button
          data-testid="tab-users"
          onClick={() => setActiveSection('users')}
          className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
            activeSection === 'users'
              ? 'bg-purple-600 text-white shadow-lg'
              : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
          }`}
        >
          <Users className="w-5 h-5" />
          {t('settings.tabs.users')}
        </button>
      </div>

      {/* Language & Region Section */}
      {activeSection === 'language' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6" data-testid="lang-section">
          <div className="flex items-center gap-2 text-gray-700">
            <Globe className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-bold">{t('settings.language_section')}</h3>
          </div>
          <p className="text-sm text-gray-500">{t('settings.language_help')}</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(() => {
              const currentMode = getLanguageMode();
              const Card = ({ value, badge, label, sub, testid }) => {
                const active = currentMode === value;
                return (
                  <button
                    data-testid={testid}
                    onClick={() => applyLanguage(value)}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                      active
                        ? 'border-purple-600 bg-purple-50 shadow-md'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-xs ${
                      active ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'
                    }`}>{badge}</div>
                    <div className="text-left">
                      <p className="font-bold text-gray-900">{label}</p>
                      <p className="text-xs text-gray-500">{sub}</p>
                    </div>
                    {active && <Check className="w-5 h-5 text-purple-600 ml-auto" />}
                  </button>
                );
              };
              return (
                <>
                  <Card
                    testid="lang-en"
                    value="en"
                    badge="EN"
                    label={t('common.english', 'English')}
                    sub="English (US)"
                  />
                  <Card
                    testid="lang-ar"
                    value="ar"
                    badge="AR"
                    label={t('common.arabic', 'العربية')}
                    sub="العربية (السعودية)"
                  />
                  <Card
                    testid="lang-both"
                    value="both"
                    badge="AR·EN"
                    label={t('common.bilingual', 'Both Languages')}
                    sub={t('common.bilingual_sub', 'English + العربية (numbers in English)')}
                  />
                </>
              );
            })()}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
            <div className="flex items-center gap-3 p-4 rounded-xl bg-gray-50">
              <MapPinIcon className="w-5 h-5 text-gray-600" />
              <div>
                <p className="text-xs text-gray-500">{t('settings.region')}</p>
                <p className="font-semibold text-gray-900">{t('settings.saudi_arabia')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-xl bg-gray-50">
              <Clock className="w-5 h-5 text-gray-600" />
              <div>
                <p className="text-xs text-gray-500">{t('settings.timezone')}</p>
                <p className="font-semibold text-gray-900">{t('settings.riyadh_timezone')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Role Permissions Section */}
      {activeSection === 'roles' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-6 text-gray-600">
            <Shield className="w-5 h-5" />
            <p>Configure which pages each role can access. Dashboard is always accessible.</p>
          </div>

          {/* Role Tabs */}
          <div className="flex flex-wrap gap-2 mb-6">
            {roles.map(role => (
              <button
                key={role.id}
                onClick={() => setActiveRole(role.id)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  activeRole === role.id
                    ? `${role.color} text-white shadow-lg`
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {role.label}
              </button>
            ))}
          </div>

          {/* Active Role Permissions */}
          <div className="border border-gray-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {roles.find(r => r.id === activeRole)?.label} Permissions
              </h3>
              <div className="flex gap-2">
                <button onClick={() => selectAll(activeRole)} className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200">
                  Select All
                </button>
                <button onClick={() => deselectAll(activeRole)} className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200">
                  Deselect All
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {availablePages.map(page => {
                const isEnabled = permissions[activeRole]?.pages?.includes(page);
                const isDashboard = page === 'dashboard';
                
                return (
                  <button
                    key={page}
                    onClick={() => togglePage(activeRole, page)}
                    disabled={isDashboard}
                    className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                      isEnabled ? 'bg-green-50 border-green-300 text-green-800' : 'bg-gray-50 border-gray-200 text-gray-500'
                    } ${isDashboard ? 'cursor-not-allowed opacity-75' : 'hover:shadow-md cursor-pointer'}`}
                  >
                    <div className={`w-5 h-5 rounded flex items-center justify-center ${isEnabled ? 'bg-green-500 text-white' : 'bg-gray-300'}`}>
                      {isEnabled ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    </div>
                    <span className="font-medium text-sm">{pageLabels[page] || page}</span>
                  </button>
                );
              })}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => savePermissions(activeRole)}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Permissions
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Employee Permissions Section */}
      {activeSection === 'users' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-6 text-gray-600">
            <UserCog className="w-5 h-5" />
            <p>Customize permissions for individual employees. This overrides role-based permissions.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* User List */}
            <div className="border border-gray-200 rounded-xl p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Employees</h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {userPermissions.filter(u => u.role !== 'admin').map(user => (
                  <div
                    key={user.user_id}
                    className={`w-full p-3 rounded-lg transition-all ${
                      selectedUser === user.user_id
                        ? 'bg-purple-100 border-purple-300 border'
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <button
                        onClick={() => setSelectedUser(user.user_id)}
                        className="flex-1 text-left"
                      >
                        <div className="font-medium text-gray-900">{user.full_name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            roles.find(r => r.id === user.role)?.color || 'bg-gray-500'
                          } text-white`}>
                            {roles.find(r => r.id === user.role)?.label || user.role}
                          </span>
                          {user.has_custom_permissions && (
                            <span className="text-xs px-2 py-0.5 rounded bg-yellow-100 text-yellow-700">
                              Custom
                            </span>
                          )}
                        </div>
                      </button>
                      <div className="flex items-center gap-1 ml-2">
                        <button
                          onClick={() => {
                            setSelectedUser(user.user_id);
                          }}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit Permissions"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {isAdmin && (
                          <button
                            onClick={async () => {
                              if (window.confirm(`Are you sure you want to delete ${user.full_name}?`)) {
                                try {
                                  await axiosInstance.delete(`/users/${user.user_id}`);
                                  toast.success('User deleted successfully');
                                  fetchPermissions();
                                } catch (error) {
                                  toast.error('Failed to delete user');
                                }
                              }
                            }}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete User"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* User Permissions Editor */}
            <div className="lg:col-span-2 border border-gray-200 rounded-xl p-4">
              {selectedUser ? (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {userPermissions.find(u => u.user_id === selectedUser)?.full_name}'s Permissions
                    </h3>
                    <button
                      onClick={() => resetUserPermissions(selectedUser)}
                      className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      Reset to Role Default
                    </button>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {availablePages.map(page => {
                      const user = userPermissions.find(u => u.user_id === selectedUser);
                      const isEnabled = user?.pages?.includes(page);
                      const isDashboard = page === 'dashboard';
                      
                      return (
                        <button
                          key={page}
                          onClick={() => toggleUserPage(selectedUser, page)}
                          disabled={isDashboard}
                          className={`flex items-center gap-2 p-2 rounded-lg border transition-all text-sm ${
                            isEnabled ? 'bg-green-50 border-green-300 text-green-800' : 'bg-gray-50 border-gray-200 text-gray-500'
                          } ${isDashboard ? 'cursor-not-allowed opacity-75' : 'hover:shadow-md cursor-pointer'}`}
                        >
                          <div className={`w-4 h-4 rounded flex items-center justify-center ${isEnabled ? 'bg-green-500 text-white' : 'bg-gray-300'}`}>
                            {isEnabled ? <Check className="w-2 h-2" /> : <X className="w-2 h-2" />}
                          </div>
                          <span className="font-medium">{pageLabels[page] || page}</span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={() => saveUserPermissions(selectedUser)}
                      disabled={saving}
                      className="flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                    >
                      {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Save User Permissions
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  <p>Select an employee to customize their permissions</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Danger Zone — System Reset (admin only) */}
      {isAdmin && (
        <div className="mt-8 bg-white rounded-xl border-2 border-rose-200 shadow-sm overflow-hidden" data-testid="danger-zone">
          <div className="bg-gradient-to-r from-rose-50 to-red-50 px-6 py-4 border-b border-rose-200 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-rose-700" />
            <h3 className="font-bold text-rose-900">Danger Zone / منطقة الخطر</h3>
          </div>
          <div className="p-6 flex items-center justify-between gap-4">
            <div>
              <h4 className="font-semibold text-gray-800">{t('settings.system_reset')}</h4>
              <p className="text-sm text-gray-600 mt-1">
                حذف كامل لجميع البيانات التشغيلية (المشاريع، الفواتير، المصروفات،
                المقاولين من الباطن، إلخ) للبدء من جديد في بيئة الإنتاج.
                يتم إنشاء نسخة احتياطية تلقائياً قبل التنفيذ.
              </p>
            </div>
            <button
              onClick={() => navigate('/system-reset')}
              className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border-2 border-rose-300 text-rose-700 hover:bg-rose-50 font-semibold text-sm"
              data-testid="open-system-reset-btn"
            >
              <Trash2 className="w-4 h-4" />
              فتح صفحة إعادة التعيين
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
